import {
  LDContext,
  LDProvider,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import {
  ComponentType,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { loadEnv } from "@smart/bridge-env-dom";
import {
  extractPrefixedTeamId,
  regionToCountry,
} from "@smart/bridge-types-basic";
import { Region, v4 } from "@smart/itops-utils-basic";

import {
  defaultLocalSmokeballBillingFeatureFlags,
  useSmokeballBillingFeatureFlags,
} from "./hooks";
import {
  SmokeballLDIdentity,
  SmokeballBillingLaunchDarkly,
  SmokeballBillingFeatureFlags,
} from "./types";
import { skipFeatureFlags } from "./utils";

export const SBBillingFeatureFlagContext =
  createContext<SmokeballBillingLaunchDarkly>({
    featureFlags: {
      bb12038: false,
    },
    setIdentity: () => {},
  });

type SmokeballBillingLDClientProps = {
  setSmokeballBillingFlags: React.Dispatch<
    React.SetStateAction<SmokeballBillingFeatureFlags>
  >;
  identity: SmokeballLDIdentity | undefined;
};

export const SmokeballBillingLDClient = ({
  setSmokeballBillingFlags,
  identity,
}: SmokeballBillingLDClientProps) => {
  const client = useLDClient();
  const sbBillingFeatureFlagsValue = useSmokeballBillingFeatureFlags();
  const region = loadEnv("Region") as Region;

  const identify = async () => {
    if (client && identity) {
      const context: LDContext = {
        kind: "multi",
        user: {
          key: identity.team?.uri
            ? extractPrefixedTeamId(identity.team?.uri, "sb").id
            : `anonymous-${v4()}`,
          name: identity.team?.name,
          country: regionToCountry[region],
          product: identity.team?.provider,
        },
      };

      await client.identify(context);
    }
  };

  useEffect(() => {
    if (!skipFeatureFlags()) identify().catch(console.error);
  }, [client, identity]);

  useEffect(() => {
    const sbBillingFeatureFlags = {
      bb12038: false,
    };

    try {
      sbBillingFeatureFlags.bb12038 =
        sbBillingFeatureFlagsValue.bb12038 || false;
    } catch (error) {
      console.error("Unexpected Smokeball Billing feature flag");
    }

    setSmokeballBillingFlags(sbBillingFeatureFlags);
  }, [sbBillingFeatureFlagsValue]);

  return null;
};

export const withSmokeballBillingFeatureFlags = (App: ComponentType) => () => {
  const [identity, setIdentity] = useState<SmokeballLDIdentity>();
  const [smokeballBillingFlags, setSmokeballBillingFlags] =
    useState<SmokeballBillingFeatureFlags>({
      bb12038: false,
    });

  const contextValue = useMemo(
    () => ({
      featureFlags: smokeballBillingFlags,
      setIdentity,
    }),
    [smokeballBillingFlags, setIdentity],
  );

  return (
    <SBBillingFeatureFlagContext.Provider value={contextValue}>
      <LDProvider
        clientSideID={loadEnv("SmokeballBillingLaunchDarklyClientSideId")}
      >
        <SmokeballBillingLDClient
          identity={identity}
          setSmokeballBillingFlags={setSmokeballBillingFlags}
        />
      </LDProvider>
      <App />
    </SBBillingFeatureFlagContext.Provider>
  );
};

export const withLocalSmokeballBillingFeatureFlags =
  (App: ComponentType) => () => {
    const contextValue = useMemo(
      () => ({
        featureFlags: defaultLocalSmokeballBillingFeatureFlags,
        setIdentity: () => {},
      }),
      [App],
    );

    return (
      <SBBillingFeatureFlagContext.Provider value={contextValue}>
        <App />
      </SBBillingFeatureFlagContext.Provider>
    );
  };

export const useSmokeballBillingFeatureFlagsContext = () =>
  useContext(SBBillingFeatureFlagContext);
